import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { rgba } from 'color2k';
import { Flex } from '$components/layouts';
import { BOISvg, SvgIcon } from '$components/elements/svg-icon';
import { Text } from '$components/elements/text';
import { Button } from '$components/elements/button';
import { theme } from '$theme';
import { useTranslation } from '$hooks';
import { B2CDeliveryMethod } from '$constants';
import { ensureEnding } from '$lib/ensure-ending';
import { formatString, mq } from '$lib/helpers';

type Props = {
    text?: string;
    selectedZipCode?: string;
    deliveryMethod?: number;
    isLoading?: boolean;
    onClick: () => void;
};

export const RegularDelivery = ({ text, selectedZipCode, deliveryMethod, isLoading, onClick }: Props) => {
    const { translate } = useTranslation();

    const deliveryContent: {
        title: string;
        description: string;
        icon: keyof typeof BOISvg;
    } = useMemo(() => {
        // Delivery makes same day unavailable
        if (deliveryMethod === B2CDeliveryMethod.DirectOnly) {
            return {
                title: selectedZipCode
                    ? formatString(translate('productPage.uspDelivery.regularDeliveryZipCodeTitle'), selectedZipCode)
                    : translate('productPage.uspDelivery.regularDeliveryTitle'),
                description: selectedZipCode
                    ? translate('productPage.uspDelivery.regularDeliveryZipCodeDescription')
                    : translate('productPage.uspDelivery.regularDeliveryDescription'),
                icon: 'truck',
            };
        }

        // Delivery is download
        if (deliveryMethod === B2CDeliveryMethod.Download) {
            return {
                title: translate('productPage.uspDelivery.downloadDeliveryTitle'),
                description: translate('productPage.uspDelivery.downloadDeliveryDescription'),
                icon: 'download',
            };
        }

        // Delivery is shop only
        if (deliveryMethod === B2CDeliveryMethod.ShopOnly) {
            return {
                title: translate('productPage.uspDelivery.shopOnlyTitle'),
                description: translate('productPage.uspDelivery.shopOnlyDescription'),
                icon: 'house',
            };
        }

        // Delivery is print on demand
        if (deliveryMethod === B2CDeliveryMethod.PrintOnDemand) {
            return {
                title: translate('productPage.uspDelivery.printOnDemandTitle'),
                description: translate('productPage.uspDelivery.printOnDemandDescription'),
                icon: 'truck',
            };
        }

        return {
            title: translate('productPage.uspDelivery.regularDeliveryTitle'),
            description: translate('productPage.uspDelivery.regularDeliveryDescription'),
            icon: 'truck',
        };
    }, [selectedZipCode, deliveryMethod]);

    return (
        <Container>
            <SvgIcon svg={deliveryContent.icon} size={24} />
            <Flex gap="medium" column grow>
                <Flex column gap="extrasmall">
                    <Text variant="smallHeader" fontWeight="Bold">
                        {deliveryContent.title}
                    </Text>
                    <Text color={theme.colors.textSubtle}>
                        {text ? ensureEnding(text) : deliveryContent.description}
                    </Text>
                </Flex>
                <Button width="expansive" variant="primary" loading={isLoading} onClick={onClick}>
                    {translate('productPage.uspDelivery.buyNowButtonText')}
                </Button>
            </Flex>
        </Container>
    );
};

const Container = styled.li(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: rgba(0, 0, 0, 0.01),
    padding: `${theme.space[7]} ${theme.space[5]} ${theme.space[8]} ${theme.space[5]}`,
    gap: theme.space[5],

    [mq('frame')]: {
        padding: `${theme.space[9]} ${theme.space[10]} ${theme.space[8]} ${theme.space[7]}`,
    },
}));
